import { FC, useMemo } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { useSkillIcon } from '../../custom-hooks/useSkillIcon';
import SkillHistoryChip from './SkillHistoryChip';
import {
  getChipTypeFromSubstateId,
  REQUIRED_SUBSTATE_IDS,
  SkillHistoryEntry,
  getInferredChipText,
  extractNumericSubstateId,
} from '../../utils/skillHistoryUtils';
import { useAppSelector } from '../../redux/hooks';
import { selectedTargetGroup } from '../../redux/reducers/targetGroupSlice';
import { useStudentSkillStates } from '../../custom-hooks/useStudentSkillStates';
import { Link } from 'react-router-dom';

// Component to render skill icon using the hook
const SkillIcon: FC<{ targetSkillType?: string | null }> = ({ targetSkillType }) => {
  const { IconComponent } = useSkillIcon({
    targetSkillType: targetSkillType || undefined,
    className: 'tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center',
  });
  return <IconComponent />;
};

const StudentRecentHistory: FC = () => {
  const { studentSkillStates } = useStudentSkillStates({ includeExtensions: true });
  const targetGroupRedux = useAppSelector(selectedTargetGroup);

  const recentSkills = useMemo(() => {
    if (!studentSkillStates || studentSkillStates.length === 0) {
      return [];
    }

    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    const filteredSkills = studentSkillStates
      .filter(skill => {
        // Check if the activity is recent (last 30 days)
        const dateString = skill.modifiedOn || skill.createdOn || '';
        const activityDate = new Date(dateString);
        const isValidDate = !isNaN(activityDate.getTime());
        const isRecent = isValidDate && activityDate >= thirtyDaysAgo;

        // Check if skill has a required substate
        const rawSubstateId = skill.skillSubstate?.id;
        const numericSubstateId = extractNumericSubstateId(rawSubstateId);
        const hasRequiredSubstate = REQUIRED_SUBSTATE_IDS.includes(numericSubstateId);

        // Check if skill is part of the current target group
        const targetGroupMatches = skill.skill.targetSkills?.some(
          ts => ts.target.targetGroup?.id === targetGroupRedux?.id,
        );

        return hasRequiredSubstate && isRecent && targetGroupMatches;
      })
      .map(skill => {
        const rawSubstateId = skill.skillSubstate?.id;
        const numericSubstateId = extractNumericSubstateId(rawSubstateId);
        const chipType = getChipTypeFromSubstateId(numericSubstateId);

        const dateString = skill.modifiedOn || skill.createdOn || '';
        const activityDate = new Date(dateString);
        const isValidDate = !isNaN(activityDate.getTime());

        return {
          skillId: skill.skill.id,
          skillName: skill.skill.name || '',
          skillCode: skill.skill.code || '',
          activityDate: isValidDate ? activityDate : new Date(),
          chipType,
          assessmentScore: skill.skillAssessment?.score,
          maxScore: skill.skillAssessment?.maxScore || 10,
          targetSkillType: skill.targetSkillType,
          skillSubstateId: numericSubstateId,
          skillSubstateName: skill.skillSubstate?.name,
          inferredBy: chipType === 'inferred' ? getInferredChipText(numericSubstateId) : undefined,
        };
      })
      .sort((a, b) => b.activityDate.getTime() - a.activityDate.getTime());

    return filteredSkills;
  }, [studentSkillStates, targetGroupRedux?.id]);

  // Group skills by date for display
  const groupSkillsByDate = () => {
    const groups: { [key: string]: SkillHistoryEntry[] } = {};

    recentSkills.forEach(entry => {
      const dateKey = entry.activityDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });

      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(entry);
    });

    return groups;
  };

  const groupedSkills = groupSkillsByDate();

  return (
    <div className="tw-mt-8 tw-w-full">
      <h3 className="tw-text-lg tw-font-bold tw-text-center tw-mb-4 tw-text-gray-800">Your recent progress</h3>

      {recentSkills.length === 0 ? (
        <div className="tw-text-sm tw-text-gray-500 tw-italic tw-py-8 tw-text-center">
          No recent skill activity in the last 30 days
        </div>
      ) : (
        <div className="tw-space-y-6 tw-max-h-[535px] tw-overflow-y-auto tw-overflow-x-hidden">
          {Object.entries(groupedSkills).map(([dateKey, entries]) => (
            <div key={dateKey}>
              <p className="tw-text-sm tw-font-bold tw-font-atkinson tw-text-neutral-500 tw-mb-3 tw-mt-0">{dateKey}</p>
              <div className="tw-space-y-3">
                {entries.map(entry => (
                  <div
                    key={entry.skillId}
                    className={`tw-flex tw-flex-col tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border tw-bg-white !tw-py-4 !tw-px-5 !tw-gap-2 dashboard-card ${
                      entry.isNested ? 'tw-ml-6 tw-border-l-4 tw-border-l-blue-200' : ''
                    }`}
                  >
                    <div className="tw-flex tw-items-center tw-w-full tw-gap-3 tw-flex-1">
                      <SkillIcon targetSkillType={entry.targetSkillType} />

                      <div className="tw-flex-1 tw-min-w-0">
                        <div className="tw-text-sm tw-text-gray-900 tw-justify-center tw-font-bold tw-leading-normal tw-whitespace-normal tw-break-words">
                          {entry.skillName || `Skill ${entry.skillId}`}
                        </div>
                      </div>
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-w-full">
                      <SkillHistoryChip entry={entry} />
                      <Link
                        className="tw-text-gray-600 hover:tw-text-gray-700 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4"
                        aria-label="Open skill"
                        to={`/skill/${entry.skillCode}`}
                      >
                        <ArrowRightIcon className="tw-w-4 tw-h-4" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentRecentHistory;
